import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { AddToWaitlistBody, SeatWaitlistResult, WaitlistEntry } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const WAITLIST_PATH = '/api/restaurants/{restaurantId}/waitlist';

const waitlistKey = (restaurantId: string) => ['waitlist', restaurantId] as const;

function useRid(): string {
  return useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
}

/** The live queue (waiting + notified), oldest first. Polls every 15s so wait
 *  times + aging stay current on the host stand without a manual refresh. */
export function useWaitlist() {
  const restaurantId = useRid();
  return useQuery({
    queryKey: waitlistKey(restaurantId),
    queryFn: async () =>
      unwrapResult<WaitlistEntry[]>(
        await openapiClient.GET(WAITLIST_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
    refetchInterval: 1000 * 15,
  });
}

export function useAddToWaitlist() {
  const restaurantId = useRid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddToWaitlistBody) =>
      unwrapResult<WaitlistEntry>(
        await openapiClient.POST(WAITLIST_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: waitlistKey(restaurantId) }),
  });
}

export function useNotifyWhenReady() {
  const restaurantId = useRid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) =>
      unwrapResult<WaitlistEntry>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/waitlist/{entryId}/notify', {
          params: { path: { restaurantId, entryId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: waitlistKey(restaurantId) }),
  });
}

export function useSeatWaitlist() {
  const restaurantId = useRid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId, tableId }: { entryId: string; tableId: string }) =>
      unwrapResult<SeatWaitlistResult>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/waitlist/{entryId}/seat', {
          params: { path: { restaurantId, entryId } },
          body: { tableId },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waitlistKey(restaurantId) });
      // Seating opened a table session - refresh the floor too.
      queryClient.invalidateQueries({ queryKey: ['table-ordering', restaurantId, 'tables'] });
    },
  });
}

export function useCancelWaitlist() {
  const restaurantId = useRid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) =>
      unwrapResult<WaitlistEntry>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/waitlist/{entryId}/cancel', {
          params: { path: { restaurantId, entryId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: waitlistKey(restaurantId) }),
  });
}

export function useMarkNoShow() {
  const restaurantId = useRid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) =>
      unwrapResult<WaitlistEntry>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/waitlist/{entryId}/no-show', {
          params: { path: { restaurantId, entryId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: waitlistKey(restaurantId) }),
  });
}
